import React, { useCallback, useEffect, useState } from 'react';
import { ClipboardList, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { eventService } from '@/lib/api/event.service';
import type {
  EventListItem,
  EventListParticipationMode,
  EventListTemplateKey,
  EventListWithItems,
} from '@om/shared-types';
import * as eventListConfig from '@om/shared-types/event-list';

const { EVENT_LIST_TEMPLATES } = eventListConfig;

const MODE_LABELS: Record<EventListParticipationMode, string> = {
  view: 'View only',
  signup: 'Sign-up',
  contribute: 'Guests add entries',
};

const ITEM_STATUS_BADGE_VARIANT: Record<string, 'secondary' | 'active' | 'completed'> = {
  open: 'secondary',
  claimed: 'active',
  fulfilled: 'completed',
  contributed: 'active',
};

type ListDraft = {
  templateKey: EventListTemplateKey | 'custom';
  name: string;
  mode: EventListParticipationMode;
};

type ItemDraft = {
  title: string;
  quantity: number;
};

interface EventListsPanelProps {
  eventId?: number | null;
}

export const EventListsPanel: React.FC<EventListsPanelProps> = ({ eventId }) => {
  const [lists, setLists] = useState<EventListWithItems[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [listDraft, setListDraft] = useState<ListDraft>({ templateKey: 'drinks', name: '', mode: 'signup' });
  const [itemDrafts, setItemDrafts] = useState<Record<number, ItemDraft>>({});
  const [lastCheckedAt, setLastCheckedAt] = useState<Date | null>(null);

  const availableTemplates = EVENT_LIST_TEMPLATES;

  const loadLists = useCallback(async () => {
    if (!eventId) {
      setLists([]);
      return;
    }

    try {
      setLoading(true);
      const data = await eventService.getEventLists(eventId);
      setLists(data);
      setError(null);
      setLastCheckedAt(new Date());
    } catch {
      setLists([]);
      setError('Could not load event lists.');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    void loadLists();
  }, [loadLists]);

  const resolveError = (err: unknown, fallback: string): string => {
    const apiError = err as { response?: { data?: { message?: string } } };
    return apiError?.response?.data?.message || fallback;
  };

  const handleCreateList = async () => {
    if (!eventId) return;

    const template = availableTemplates.find((entry) => entry.key === listDraft.templateKey);
    const isCustom = listDraft.templateKey === 'custom';
    const name = (isCustom ? listDraft.name : template?.name ?? '').trim();

    if (!name) {
      setError(isCustom ? 'Enter a name for the custom list.' : 'Choose a list template.');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await eventService.createEventList(eventId, {
        name,
        template_key: template?.key ?? null,
        participation_mode: isCustom ? listDraft.mode : template?.participation_mode,
      });
      setListDraft({ templateKey: 'drinks', name: '', mode: 'signup' });
      await loadLists();
    } catch (err) {
      setError(resolveError(err, 'Failed to create list.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteList = async (listId: number) => {
    if (!eventId) return;

    try {
      setSaving(true);
      setError(null);
      await eventService.deleteEventList(eventId, listId);
      await loadLists();
    } catch (err) {
      setError(resolveError(err, 'Failed to delete list.'));
    } finally {
      setSaving(false);
    }
  };

  const handleAddItem = async (list: EventListWithItems) => {
    if (!eventId) return;

    const draft = itemDrafts[list.id] ?? { title: '', quantity: 1 };
    if (!draft.title.trim()) {
      setError('Enter an item title first.');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await eventService.addEventListItem(eventId, list.id, {
        title: draft.title.trim(),
        quantity: draft.quantity,
      });
      setItemDrafts((current) => ({ ...current, [list.id]: { title: '', quantity: 1 } }));
      await loadLists();
    } catch (err) {
      setError(resolveError(err, 'Failed to add item.'));
    } finally {
      setSaving(false);
    }
  };

  const handleItemAction = async (listId: number, itemId: number, action: 'release' | 'fulfill' | 'reassign' | 'delete') => {
    if (!eventId) return;

    let claimedByName = '';
    if (action === 'reassign') {
      claimedByName = window.prompt('Assign this item to (name):')?.trim() ?? '';
      if (!claimedByName) {
        return;
      }
    }

    try {
      setSaving(true);
      setError(null);
      if (action === 'release') {
        await eventService.releaseEventListItem(eventId, listId, itemId);
      } else if (action === 'fulfill') {
        await eventService.fulfillEventListItem(eventId, listId, itemId);
      } else if (action === 'reassign') {
        await eventService.reassignEventListItem(eventId, listId, itemId, claimedByName);
      } else {
        await eventService.deleteEventListItem(eventId, listId, itemId);
      }
      await loadLists();
    } catch (err) {
      setError(resolveError(err, `Failed to ${action} item.`));
    } finally {
      setSaving(false);
    }
  };

  const renderItem = (list: EventListWithItems, item: EventListItem) => (
    <div key={item.id} className="rounded-lg border border-slate-200 p-2.5 text-xs space-y-1.5 bg-slate-50/50">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="font-medium text-slate-900 truncate">{item.title}</div>
          <div className="text-[11px] text-muted-foreground">
            {item.category} · qty {item.quantity}
            {item.claimed_by_name ? ` · ${item.claimed_by_name}` : ''}
            {item.contributed_by_name ? ` · added by ${item.contributed_by_name}` : ''}
          </div>
          {item.cost_note && <div className="text-[11px] text-muted-foreground">{item.cost_note}</div>}
        </div>
        <Badge variant={ITEM_STATUS_BADGE_VARIANT[item.status] ?? 'secondary'} size="sm" className="capitalize text-[10px] shrink-0">
          {item.status}
        </Badge>
      </div>
      <div className="flex flex-wrap gap-1 pt-1">
        {list.participation_mode === 'signup' && item.status === 'claimed' && (
          <Button variant="outline" size="xs" className="h-6 text-[11px] px-2" onClick={() => handleItemAction(list.id, item.id, 'fulfill')}>
            Fulfill
          </Button>
        )}
        {list.participation_mode === 'signup' && item.status !== 'open' && item.status !== 'contributed' && (
          <Button variant="outline" size="xs" className="h-6 text-[11px] px-2" onClick={() => handleItemAction(list.id, item.id, 'release')}>
            Release
          </Button>
        )}
        {list.participation_mode === 'signup' && (
          <Button variant="outline" size="xs" className="h-6 text-[11px] px-2" onClick={() => handleItemAction(list.id, item.id, 'reassign')}>
            Reassign
          </Button>
        )}
        <Button variant="ghost" size="xs" className="h-6 text-[11px] px-1.5 text-destructive hover:bg-destructive/10" onClick={() => handleItemAction(list.id, item.id, 'delete')}>
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );

  if (!eventId) {
    return <div className="text-xs text-muted-foreground py-2">Add an event first to create lists.</div>;
  }

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-2.5 space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <select
            className="rounded-md border border-slate-300 px-2.5 py-1.5 text-xs bg-white"
            value={listDraft.templateKey}
            onChange={(event) => {
              const templateKey = event.target.value as ListDraft['templateKey'];
              setListDraft((current) => ({ ...current, templateKey, name: templateKey === 'custom' ? current.name : '' }));
            }}
          >
            {availableTemplates.map((template) => (
              <option key={template.key} value={template.key}>{template.name}</option>
            ))}
            <option value="custom">Custom list</option>
          </select>
          {listDraft.templateKey === 'custom' ? (
            <select
              className="rounded-md border border-slate-300 px-2.5 py-1.5 text-xs bg-white"
              value={listDraft.mode}
              onChange={(event) => setListDraft((current) => ({ ...current, mode: event.target.value as EventListParticipationMode }))}
            >
              {(Object.keys(MODE_LABELS) as EventListParticipationMode[]).map((mode) => (
                <option key={mode} value={mode}>{MODE_LABELS[mode]}</option>
              ))}
            </select>
          ) : (
            <div className="flex items-center text-[11px] text-muted-foreground px-1">
              {MODE_LABELS[availableTemplates.find((entry) => entry.key === listDraft.templateKey)?.participation_mode ?? 'signup']}
            </div>
          )}
        </div>
        {listDraft.templateKey === 'custom' && (
          <input
            className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-xs"
            value={listDraft.name}
            onChange={(event) => setListDraft((current) => ({ ...current, name: event.target.value }))}
            placeholder="List name (e.g. Ice & Coolers)"
          />
        )}
        <Button size="xs" className="h-7 text-xs" onClick={handleCreateList} disabled={saving || loading}>
          <Plus className="h-3 w-3 mr-1" /> Add List
        </Button>
      </div>

      {error && <div className="text-xs text-destructive">{error}</div>}

      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] text-muted-foreground">
          {lastCheckedAt ? `Last checked ${lastCheckedAt.toLocaleTimeString()}` : ''}
        </span>
        <Button variant="outline" size="xs" className="h-6 text-[11px] px-2" onClick={() => void loadLists()} disabled={loading}>
          <RefreshCw className="h-3 w-3 mr-1" /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="text-xs text-muted-foreground">Loading lists...</div>
      ) : lists.length === 0 ? (
        <div className="text-xs text-muted-foreground py-2">No lists yet. Add a standard or custom list above.</div>
      ) : (
        <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
          {lists.map((list) => {
            const draft = itemDrafts[list.id] ?? { title: '', quantity: 1 };
            return (
              <div key={list.id} className="rounded-lg border border-slate-200 p-2.5 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <ClipboardList className="h-3.5 w-3.5 text-sky-600 shrink-0" />
                    <span className="text-xs font-semibold text-slate-900 truncate">{list.name}</span>
                    <Badge variant="secondary" size="sm" className="text-[10px] shrink-0">
                      {MODE_LABELS[list.participation_mode]}
                    </Badge>
                  </div>
                  <Button variant="ghost" size="xs" className="h-6 text-[11px] px-1.5 text-destructive hover:bg-destructive/10" onClick={() => handleDeleteList(list.id)}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
                {list.description && <div className="text-[11px] text-muted-foreground">{list.description}</div>}

                <div className="flex items-center gap-2">
                  <input
                    className="flex-1 rounded-md border border-slate-300 px-2.5 py-1.5 text-xs"
                    value={draft.title}
                    onChange={(event) => setItemDrafts((current) => ({ ...current, [list.id]: { ...draft, title: event.target.value } }))}
                    placeholder={list.participation_mode === 'contribute' ? 'Seed an entry (guests add their own)' : 'Item title'}
                  />
                  {list.participation_mode === 'signup' && (
                    <input
                      type="number"
                      min={1}
                      className="w-16 rounded-md border border-slate-300 px-2.5 py-1.5 text-xs"
                      value={draft.quantity}
                      onChange={(event) => setItemDrafts((current) => ({ ...current, [list.id]: { ...draft, quantity: Number(event.target.value) || 1 } }))}
                    />
                  )}
                  <Button size="xs" className="h-7 text-xs" onClick={() => handleAddItem(list)} disabled={saving || loading}>
                    <Plus className="h-3 w-3 mr-1" /> Add
                  </Button>
                </div>

                {list.items.length === 0 ? (
                  <div className="text-[11px] text-muted-foreground">No items yet.</div>
                ) : (
                  <div className="space-y-2">
                    {list.items.map((item) => renderItem(list, item))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EventListsPanel;
